import { useEffect } from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
} from '@monksflow/monks-ui';

import type { PublishOutcome } from '@/shared/hooks/usePublishTemplate';
import { useTranslation } from 'react-i18next';

/** How long the success state stays up before the modal auto-closes. */
const SUCCESS_AUTO_CLOSE_MS = 3000;

export interface PublishTemplateModalProps {
  open: boolean;
  /** Derived publish feedback (idle/pending/success/error) from usePublishTemplate. */
  outcome: PublishOutcome;
  onClose: () => void;
}

/**
 * Publish feedback for the templates-agent email. The asset name is no longer prompted (the caller
 * derives it) — this modal only reflects the in-flight publish: a spinner while pending, a success
 * message that auto-closes after a few seconds, or the real pipeline error(s) (kept open) on failure.
 * A success that carries non-fatal warnings stays open and lists them, so they aren't missed.
 */
export function PublishTemplateModal({ open, outcome, onClose }: PublishTemplateModalProps) {
  const { t } = useTranslation('contentTest');

  const isPending = outcome.kind === 'pending';
  const isSuccess = outcome.kind === 'success';
  const hasWarnings = outcome.kind === 'success' && outcome.warnings.length > 0;

  // Auto-close shortly after a *clean* success. When there are warnings, keep the modal open so the
  // user reads them and dismisses manually.
  useEffect(() => {
    if (!open || !isSuccess || hasWarnings) return;
    const timer = setTimeout(onClose, SUCCESS_AUTO_CLOSE_MS);
    return () => clearTimeout(timer);
  }, [open, isSuccess, hasWarnings, onClose]);

  // While pending, ignore outside-click/escape close so we don't orphan an in-flight publish.
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && !isPending) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="gap-0 p-4 max-w-120"
        showCloseButton={!isPending}
        onEscapeKeyDown={(event) => {
          if (isPending) event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (isPending) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (isPending) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{t('emailFlowWidget.publishTemplateModal.publishingTemplate')}</DialogTitle>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-4 pb-6 pt-2">
          {isPending && (
            <div className="flex items-center gap-3 text-sm">
              <Spinner />
              <span>{t('emailFlowWidget.publishTemplateModal.publishingArtifact')}</span>
            </div>
          )}

          {/* One neutral alert for success. The title flips to "…with warnings" and the non-fatal
              warnings are listed below the summary (the `Alert` type here has no "warning" variant). */}
          {outcome.kind === 'success' && (
            <Alert variant="default">
              <AlertTitle>
                {hasWarnings
                  ? t('emailFlowWidget.publishTemplateModal.publishedWithWarnings')
                  : t('emailFlowWidget.publishTemplateModal.published')}
              </AlertTitle>
              <AlertDescription>
                <div>
                  {t('emailFlowWidget.publishTemplateModal.publishedToPipelines', {
                    count: outcome.published,
                  })}
                </div>
                {outcome.warnings.length === 1 && <div className="mt-2">{outcome.warnings[0]}</div>}
                {outcome.warnings.length > 1 && (
                  <ul className="mt-2 list-disc pl-4">
                    {outcome.warnings.map((warning: string, i: number) => (
                      // Warnings can legitimately duplicate (same issue across pipelines), so the string
                      // isn't a unique key — index-prefix it (the list is stable and never reorders).
                      <li key={`${i}-${warning}`}>{warning}</li>
                    ))}
                  </ul>
                )}
              </AlertDescription>
            </Alert>
          )}

          {outcome.kind === 'error' && (
            <Alert variant="destructive">
              <AlertTitle>{t('emailFlowWidget.publishTemplateModal.publishFailed')}</AlertTitle>
              {/* The variant's own description colour is `*:`-scoped and never reaches this far
                  down, so without saying it here the failure detail renders in muted grey. */}
              <AlertDescription className="text-destructive">
                {outcome.messages.length === 1 ? (
                  outcome.messages[0]
                ) : (
                  <ul className="list-disc pl-4">
                    {outcome.messages.map((message: string) => (
                      <li key={message}>{message}</li>
                    ))}
                  </ul>
                )}
              </AlertDescription>
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isPending}
            data-testid="publish-modal-close"
          >
            {isSuccess
              ? t('emailFlowWidget.publishTemplateModal.done')
              : t('emailFlowWidget.publishTemplateModal.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
